<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    // Components
    import { Player } from '@oat-sa-private/ui-components';

    // Utils
    import { createLastPressedKeyListener, wrapWithLogger } from '../util/analytics.js';
    import { noop } from 'lodash';
    import { get } from '../../util/object.js';
    import { getInteractionStateStore } from '../../itemsStateStore.js';

    // Player props
    // `plays, startTime, disabled` must not be in $$restProps, because Player modifies them internally:
    //  if any other unrelated props inside $$restProps changes, svelte will trigger their reactive callbacks too,
    //  and Player's internal modification will be overwritten
    let {
        plays,
        startTime,
        disabled,
        autostart,
        loop,
        maxPlays,
        isLinear,
        allowFullscreen,
        src,
        type,
        width,
        tracks,
        controlsOverride,
        allowPause = true, //default value of `Player`
        dir,
        feedbackLang,

        //Logging props
        itemIdentifier,
        responseIdentifier,
        onStart = noop,
        onFinish = noop,
        autostartDelayMs,
        seekLogDebounceInterval = 500,
        getInteractionElement,
        staticElementId,

        ontimeupdate,

        ...restProps
    } = $props();

    const lastPressedKeyListener = createLastPressedKeyListener();
    // svelte-ignore state_referenced_locally
    const interactionStateStore =
        itemIdentifier && responseIdentifier ? getInteractionStateStore(itemIdentifier, responseIdentifier) : void 0;

    // svelte-ignore state_referenced_locally
    const staticElementDetails = staticElementId && { staticElementId, componentType: 'MediaInABlock' };

    // svelte-ignore state_referenced_locally
    const handleStart = wrapWithLogger({
        getInteractionElement,
        handler: onStart,
        getDetails: event => {
            const isAutostart = get(event, 'detail.autostart');
            const pressedKey = lastPressedKeyListener.lastPressedKey;
            return {
                position: get(event, 'detail.position'),
                ...(pressedKey && { pressedKey }),
                autostart: isAutostart,
                touched: !isAutostart,
                ...(autostartDelayMs && { autostartDelay: autostartDelayMs }),
                ...staticElementDetails
            };
        },
        interactionStateStore,
        eventTypeToDomEventTypeMap: {
            start: 'play'
        }
    });

    // svelte-ignore state_referenced_locally
    const handleFinish = wrapWithLogger({
        getInteractionElement,
        handler: onFinish,
        getDetails: () => ({
            touched: false,
            ...staticElementDetails
        }),
        interactionStateStore,
        eventTypeToDomEventTypeMap: {
            finish: 'ended'
        },
        logDebounceOptions: {
            wait: seekLogDebounceInterval,
            leading: true,
            trailing: false
        }
    });

    // svelte-ignore state_referenced_locally
    const handlePause = !allowPause
        ? noop
        : wrapWithLogger({
              getInteractionElement,
              interactionStateStore,
              getDetails: event => {
                  const pressedKey = lastPressedKeyListener.lastPressedKey;
                  return {
                      position: get(event, 'detail.position'),
                      ...(pressedKey && { pressedKey }),
                      ...staticElementDetails
                  };
              }
          });

    // svelte-ignore state_referenced_locally
    const handleSeeked = wrapWithLogger({
        getInteractionElement,
        interactionStateStore,
        getDetails: event => ({
            endPosition: get(event, 'detail.position'),
            ...staticElementDetails
        }),
        logDebounceOptions: {
            wait: seekLogDebounceInterval,
            trailing: true
        }
    });

    // svelte-ignore state_referenced_locally
    const handleSeeking = wrapWithLogger({
        getInteractionElement,
        interactionStateStore,
        getDetails: event => {
            const pressedKey = lastPressedKeyListener.lastPressedKey;
            return {
                startPosition: get(event, 'detail.position'),
                ...(pressedKey && { pressedKey }),
                ...staticElementDetails
            };
        },
        logDebounceOptions: {
            wait: 500,
            leading: true,
            trailing: false
        }
    });
</script>

<Player
    {...restProps}
    {allowPause}
    {plays}
    {startTime}
    {disabled}
    {autostart}
    {loop}
    {maxPlays}
    {isLinear}
    {allowFullscreen}
    {src}
    {type}
    {width}
    {tracks}
    {controlsOverride}
    {dir}
    {feedbackLang}
    on:timeupdate={e => ontimeupdate?.(e.detail)}
    on:start={handleStart}
    on:finish={handleFinish}
    on:seeked={handleSeeked}
    on:seeking={handleSeeking}
    on:pause={handlePause}
    on:keydown={lastPressedKeyListener.saveLastPressedKey} />
